import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, computed, HostBinding, HostListener, inject, input, OnDestroy, TemplateRef, Type } from '@angular/core';
import { DropdownService } from './dropdown.service';
import { DropdownContent, DropdownEvent, DropdownOptions } from './model';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-dropdown',
  imports: [NgComponentOutlet, NgTemplateOutlet],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.css'
})
export class DropdownComponent implements AfterViewInit, OnDestroy {

  content = input.required<DropdownContent>()
  options = input.required<DropdownOptions>()

  private eventsSubject = new Subject<DropdownEvent>()
  readonly events$ = this.eventsSubject.asObservable()

  protected contentComponent = computed(() => {
    const content = this.content()
    if (content instanceof Type) {
      return content
    }
    return undefined
  })

  protected contentTemplate = computed(() => {
    const content = this.content()
    if (content instanceof TemplateRef) {
      return content
    }
    return undefined
  })

  protected contentString = computed(() => {
    const content = this.content()
    if (typeof(content) == 'string') {
      return content
    }
    return undefined
  })

  private cssClass = computed(() => this.options().cssClass ?? '')
  private cssStyle = computed(() => this.resolveCssStyle(this.options()))
  private positionClass = computed(() => this.resolvePositionClass(this.options()))

  private dropdown = inject(DropdownService)

  @HostBinding('class') get hostCssClass() {
    return this.cssClass() + ' ' + this.positionClass()
  }

  @HostBinding('style') get hostCssStyle() {
    return this.cssStyle()
  }

  ngAfterViewInit(): void {
    this.dropdown.registerDropdown(this)
    this.eventsSubject.next({show: true})
  }

  ngOnDestroy(): void {
    this.eventsSubject.next({show: false})
    this.eventsSubject.complete()
  }

  @HostListener('click')
  onClick() {
    if (this.options().destroyOnClick) {
      this.dropdown.destroy()
    }
  }

  private resolvePositionClass(options: DropdownOptions | undefined) {
    const arr: string[] = []
    if (typeof(options?.positionX) == 'string') {
      arr.push(`dropdown-position-${options.positionX}`)
    }
    if (typeof(options?.positionY) == 'string') {
      arr.push(`dropdown-position-${options.positionY}`)
    }
    return arr.join(' ')
  }

  private resolveCssStyle(options: DropdownOptions | undefined) {
    const res: Record<string, string> = {}

    if (typeof(options?.backgroundColor) == 'string') {
      res['backgroundColor'] = options.backgroundColor
    }

    if (typeof(options?.width) == 'number') {
      res['width'] = `${options.width}px`
    }
    else if (typeof(options?.width) == 'string') {
      res['width'] = options.width
    }
    if (typeof(options?.height) == 'number') {
      res['height'] = `${options.height}px`
    }
    else if (typeof(options?.height) == 'string') {
      res['height'] = options.height
    }

    if (typeof(options?.minWidth) == 'number') {
      res['minWidth'] = `${options.minWidth}px`
    }
    else if (typeof(options?.minWidth) == 'string') {
      res['minWidth'] = options.minWidth
    }
    if (typeof(options?.minHeight) == 'number') {
      res['minHeight'] = `${options.minHeight}px`
    }
    else if (typeof(options?.minHeight) == 'string') {
      res['minHeight'] = options.minHeight
    }

    if (typeof(options?.maxWidth) == 'number') {
      res['maxWidth'] = `${options.maxWidth}px`
    }
    else if (typeof(options?.maxWidth) == 'string') {
      res['maxWidth'] = options.maxWidth
    }
    if (typeof(options?.maxHeight) == 'number') {
      res['maxHeight'] = `${options.maxHeight}px`
    }
    else if (typeof(options?.maxHeight) == 'string') {
      res['maxHeight'] = options.maxHeight
    }

    if (typeof(options?.padding) == 'string') {
      res['padding'] = options.padding
    }
    else if (typeof(options?.padding) == 'number') {
      res['padding'] = `${options.padding}px`
    }
    else if (Array.isArray(options?.padding)) {
      res['padding'] = options.padding.map(n => `${n}px`).join(' ')
    }

    return res
  }

}
